import { useEffect } from "react";
import ArticleListBrowser from "./ArticleListBrowser";
import BookmarkedArticles from "./BookmarkedArticles";

import { Redirect, useLocation } from "react-router-dom";
import { PrivateRoute } from "../PrivateRoute";
import ClassroomArticles from "./ClassroomArticles";
import TopTabs from "../components/TopTabs";
import strings from "../i18n/definitions";

import OwnArticles from "./OwnArticles";
import ReadingHistory from "../words/WordHistory";
import MySearches from "./MySearches";
import Search from "./Search";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

import * as s from "../components/ColumnWidth.sc";
import LocalStorage from "../assorted/LocalStorage";
import { BrowsingSessionContext } from "../contexts/BrowsingSessionContext";
import useBrowsingSession from "../hooks/useBrowsingSession";
import useTabbedRoute from "../hooks/useTabbedRoute";

const READ_TAB_PATHS = [
  "/articles",
  "/articles/mySearches",
  "/articles/bookmarked",
  "/articles/classroom",
];

export default function ArticlesRouter({ hasExtension, isChrome }) {
  const { getBrowsingSessionId } = useBrowsingSession();
  const location = useLocation();
  const hideRecommendations = LocalStorage.hasFeature("hide_recommendations");
  const isStudent = LocalStorage.isStudent();

  useEffect(() => {
    if (READ_TAB_PATHS.includes(location.pathname)) {
      LocalStorage.setLastVisitedReadPath(location.pathname);
    }
  }, [location.pathname]);

  const searchIcon = (
    <span style={{ display: "inline-block", padding: "0 0.25em", verticalAlign: "middle" }}>
      <SearchRoundedIcon style={{ fontSize: "1.25rem", verticalAlign: "middle" }} />
    </span>
  );

  const tabs = [
    !hideRecommendations && { text: "Discover", link: "/articles" },
    { text: strings.myArticles, link: "/articles/bookmarked" },
    isStudent && { text: strings.classroomTab, link: "/articles/classroom" },
    !hideRecommendations && {
      text: searchIcon,
      link: "/articles/mySearches",
      // Stay active on /search too — results are conceptually the search tab.
      isActive: (_, loc) => loc.pathname === "/articles/mySearches" || loc.pathname === "/search",
    },
  ].filter(Boolean);

  const swipeRef = useTabbedRoute(
    tabs.map((t) => t.link),
    { pathAliases: { "/search": "/articles/mySearches" } },
  );

  return (
    <BrowsingSessionContext.Provider value={getBrowsingSessionId}>
      {/* Rendering top menu first, then routing to corresponding page */}
      <s.NarrowColumn>
        <TopTabs title={strings.articles} tabsAndLinks={tabs} />

        <div ref={swipeRef} style={{ minHeight: "70vh" }}>
          {hideRecommendations ? (
            <Redirect from="/articles" exact to="/articles/classroom" />
          ) : (
            <PrivateRoute
              path="/articles"
              exact
              component={ArticleListBrowser}
              hasExtension={hasExtension}
              isChrome={isChrome}
            />
          )}
          <PrivateRoute
            path="/articles/bookmarked"
            component={BookmarkedArticles}
          />
          <PrivateRoute
            path="/articles/classroom"
            component={ClassroomArticles}
          />

          <PrivateRoute path="/articles/ownTexts" component={OwnArticles} />

          <PrivateRoute path="/articles/history" component={ReadingHistory} />

          <PrivateRoute path="/articles/mySearches" component={MySearches} />

          <PrivateRoute path="/search" component={Search} />
        </div>
      </s.NarrowColumn>
    </BrowsingSessionContext.Provider>
  );
}

// Having components passed to the Search
// look for a search, boolean
// passing a different prop, to make search
// render either search or no search
